/**
 * Media queue + synced media metadata.
 *
 * Captured files are stored here as Blobs and uploaded separately from the
 * JSON outbox (see `@/lib/media/uploader`), so a 5 MB photo never blocks a
 * 200-byte diary edit. The record a photo belongs to syncs on its own; the
 * uploader waits for it to reach the server before sending the bytes.
 */
import type { MediaEntityType } from '@/lib/api/field-types';
import { assertWritable } from '@/lib/auth/view-as';

import { db, type MediaAssetRecord, type MediaQueueRecord } from './db';
import { queueFieldMutation } from './field';
import { guardStorage } from './quota';

/** `work`: something new can upload (worth a sync); `state`: only counts changed. */
export type MediaQueueChange = 'work' | 'state';
const listeners = new Set<(change: MediaQueueChange) => void>();

/** Notified when the queue changes (sync counts, auto-upload). */
export function subscribeToMediaQueue(listener: (change: MediaQueueChange) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function notifyMediaQueue(change: MediaQueueChange = 'state'): void {
  listeners.forEach((listener) => listener(change));
}

export async function pendingMediaCount(): Promise<number> {
  return db.media_queue.where('state').anyOf(['queued', 'uploading', 'failed']).count();
}

export async function enqueueMedia(record: MediaQueueRecord): Promise<void> {
  assertWritable(); // never queued while staff view as someone (read-only)
  await guardStorage(() => db.media_queue.add(record));
  notifyMediaQueue('work');
}

export async function listQueuedMedia(projectId: string): Promise<MediaQueueRecord[]> {
  const rows = await db.media_queue.where('project_id').equals(projectId).toArray();
  return rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export function listQueuedMediaFor(
  entityType: MediaEntityType,
  entityId: string,
): Promise<MediaQueueRecord[]> {
  return db.media_queue.where('[entity_type+entity_id]').equals([entityType, entityId]).toArray();
}

export async function listMediaAssets(projectId: string): Promise<MediaAssetRecord[]> {
  const rows = await db.media_assets.where('project_id').equals(projectId).toArray();
  return rows.sort((a, b) =>
    (b.data.captured_at_device ?? '').localeCompare(a.data.captured_at_device ?? ''),
  );
}

export function listMediaAssetsFor(
  entityType: MediaEntityType,
  entityId: string,
): Promise<MediaAssetRecord[]> {
  return db.media_assets
    .where('[data.entity_type+data.entity_id]')
    .equals([entityType, entityId])
    .toArray();
}

/** Every unsent upload, oldest first (the uploader's work list). */
export async function uploadCandidates(): Promise<MediaQueueRecord[]> {
  const rows = await db.media_queue
    .where('state')
    .anyOf(['queued', 'uploading', 'failed'])
    .toArray();
  return rows.sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/** When the soonest failed upload may be retried (`null`: none is waiting on a backoff). */
export async function nextMediaAttemptAt(): Promise<number | null> {
  const failed = await db.media_queue.where('state').equals('failed').toArray();
  const times = failed.flatMap((row) =>
    row.next_attempt_at === null ? [] : [row.next_attempt_at],
  );
  return times.length ? Math.min(...times) : null;
}

export async function patchMedia(
  id: string,
  patch: Partial<MediaQueueRecord>,
  change: MediaQueueChange = 'state',
): Promise<void> {
  await db.media_queue.update(id, patch);
  notifyMediaQueue(change);
}

/** Retry a failed upload now (resumes from the step it reached). */
export async function retryMedia(id: string): Promise<void> {
  await patchMedia(id, { state: 'queued', next_attempt_at: null, last_error: null }, 'work');
}

/** Upload despite the Wi-Fi-only preference. */
export async function forceMedia(id: string): Promise<void> {
  await patchMedia(id, { force: true, state: 'queued', next_attempt_at: null }, 'work');
}

/** Remove a capture that has not been sent yet. */
export async function removeQueuedMedia(id: string): Promise<void> {
  await db.media_queue
    .where('id')
    .equals(id)
    .filter((row) => row.step === 'init')
    .delete();
  notifyMediaQueue();
}

/**
 * Forget local copies the server now serves (the asset reached `available`
 * or `rejected` in a pull), keeping storage small on cheap phones.
 */
export async function pruneUploadedMedia(): Promise<void> {
  const uploaded = await db.media_queue.where('state').equals('uploaded').toArray();
  const settled: string[] = [];
  for (const row of uploaded) {
    const asset = await db.media_assets.get(row.id);
    if (asset && ['available', 'rejected'].includes(asset.data.status)) settled.push(row.id);
  }
  if (settled.length) await db.media_queue.bulkDelete(settled);
}

/** Share a photo with the owner side (or stop sharing). Offline-capable. */
export async function setMediaShared(record: MediaAssetRecord, shared: boolean): Promise<void> {
  const current = record.data.client_visible;
  if (current === shared) return;
  await queueFieldMutation('media_asset', {
    entity_id: record.id,
    project_id: record.project_id,
    operation: 'update',
    payload: { changes: { client_visible: shared }, base: { client_visible: current } },
    summary: record.data.caption || 'Photo',
  });
}
